import { useDataTable } from "../../../../../shared/hooks/DataTable";
import { InvoiceSummaryService } from "../../InvoiceSummaryService";
import { useState, useEffect } from "react";
import {
  DataPeriod,
  ODataPeriod,
  OInvoiceLookUpField,
  prepareInvoiceStatuses,
  SummaryDataDetails,
} from "../../../../../shared/constants";
import { SummaryDetailsTableProps } from "./summaryDetailsTableConsts";
import { InvoiceLookUpTableDialog } from "./lookupTableDialog";
import { useImExExcel } from "../../../../../shared/hooks/ImExExcel";
import { useToolbar } from "../../../../../shared/hooks/Toolbar";
import {
  combineSummaryDetails,
  formatVatRate,
  prepareInvoiceStatusClassMap,
  relatedInvoiceNumberMap,
} from "../../invoiceSummaryUtils";
import { IamColumn } from "../../../../../shared/hooks/DataTableBasic";
import { FormattedCurrency } from "../../../../../shared/component/FormattedCurrency";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { Column } from "primereact/column";
import { SumColumn } from "./sumColumn";
import moment from "moment";
import { useTranslation } from "../../../../../shared/language";

export const SummaryDetailsTable = ({
  formState,
  id,
  serial,
  isEditMode,
  setSummaryDetails,
  summaryDetails,
  setSelectedSerial,
}: SummaryDetailsTableProps) => {
  const { t } = useTranslation();
  const [lookupData, setLookupData] = useState<Record<string, any>[]>([]);
  const [openLookup, setOpenLookup] = useState<boolean>(false);
  const [currSummaryDetails, setCurrSummaryDetails] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalAmountWithoutVAT, setTotalAmountWithoutVAT] = useState(0);
  const [totalVatAmount, setTotalVatAmount] = useState(0);
  const [deletedItems, setDeletedItems] = useState<
    SummaryDataDetails[] | SummaryDataDetails
  >([]);

  const summaryDetailColumns: IamColumn[] = [
    {
      field: OInvoiceLookUpField.INVOICE_SERIAL,
      header: t("common.symbol"),
      width: 120,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.INVOICE_NUMBER,
      header: t("common.invoiceNo"),
      width: 120,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.INVOICE_DATE,
      header: t("common.invoiceDate"),
      width: 120,
      sortable: false,
      dataType: "custom",
      customCell: (item) => (
        <span>
          {item.invoiceDate
            ? new Date(item.invoiceDate).toLocaleDateString("vi-VN")
            : ""}
        </span>
      ),
    },
    {
      field: OInvoiceLookUpField.BUYER_NAME,
      header: t("common.buyerName"),
      width: 250,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.BUYER_TAX_CODE,
      header: t("common.buyerTaxCode"),
      width: 170,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.BUYER_BUDGET_UNIT_CODE,
      header: t("common.budgetUnitCode"),
      sortable: false,
      width: 190,
    },
    {
      field: OInvoiceLookUpField.PRODUCT_CODE,
      header: t("common.itemCode"),
      sortable: false,
      width: 100,
    },
    {
      field: OInvoiceLookUpField.PRODUCT_NAME,
      header: t("common.itemName"),
      width: 150,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.UNIT_CODE,
      header: t("catalog.unit"),
      width: 100,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.QUANTITY,
      header: t("common.quantity"),
      width: 120,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.AMOUNT_WITHOUT_VAT,
      header: t("common.totalBeforeTax"),
      width: 120,
      sortable: false,
      dataType: "custom",
      customCell: (item) => (
        <FormattedCurrency
          value={item.amountWithoutVAT}
          currency={formState.currencyCode}
        />
      ),
    },
    {
      field: OInvoiceLookUpField.VAT_RATE,
      header: t("catalog.taxRate"),
      width: 120,
      sortable: false,
      dataType: "custom",
      customCell: (item) => <span>{formatVatRate(item.vatRate)}</span>,
    },
    {
      field: OInvoiceLookUpField.TOTAL_DISCOUNT_AMOUNT,
      header: t("report.totalOtherDeductions"),
      width: 120,
      sortable: false,
      dataType: "custom",
      customCell: (item) => (
        <FormattedCurrency
          value={item.totalDiscountAmount}
          currency={formState.currencyCode}
        />
      ),
    },
    {
      field: OInvoiceLookUpField.VAT_AMOUNT,
      header: t("common.totalTax"),
      width: 120,
      sortable: false,
      dataType: "custom",
      customCell: (item) => (
        <FormattedCurrency
          value={item.vatAmount}
          currency={formState.currencyCode}
        />
      ),
    },
    {
      field: OInvoiceLookUpField.TOTAL_AMOUNT,
      header: t("common.totalPayment"),
      width: 120,
      sortable: false,
      dataType: "custom",
      customCell: (item) => (
        <FormattedCurrency
          value={item.totalAmount}
          currency={formState.currencyCode}
        />
      ),
    },
    {
      field: OInvoiceLookUpField.EXCHANGE_RATE,
      header: t("report.exchangeRate"),
      width: 120,
      sortable: false,
      dataType: "custom",
      customCell: (item) => (
        <FormattedCurrency
          value={item.exchangeRate}
          currency={formState.currencyCode}
        />
      ),
    },
    {
      field: OInvoiceLookUpField.TOTAL_FEE,
      header: t("report.totalFee"),
      width: 120,
      sortable: false,
      dataType: "custom",
      customCell: (item) => (
        <FormattedCurrency
          value={item.totalFee}
          currency={formState.currencyCode}
        />
      ),
    },
    {
      field: OInvoiceLookUpField.INVOICE_STATUS,
      header: t("common.status"),
      dataType: "custom",
      customCell: (item) => (
        <span
          className={`invoice-status invoice-status-${prepareInvoiceStatusClassMap(
            item.invoiceStatus
          )}`}
        >
          {
            t(
              prepareInvoiceStatuses.find(
                (status) => item.invoiceStatus === status.value
              )?.translationKey || "common.status"
            )
          }
        </span>
      ),
      width: 120,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.RELATED_INVOICE_TYPE,
      header: t("report.relatedInvoiceType"),
      width: 170,
      dataType: "custom",
      customCell: (item) => (
        <span>{relatedInvoiceNumberMap(item.relatedInvoiceType)}</span>
      ),
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.RELATED_INVOICE_SERIAL,
      header: t("report.relatedInvoiceTemplateNumber"),
      width: 170,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.RELATED_TEMPLATE_CODE,
      header: t("report.relatedInvoiceSymbol"),
      width: 170,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.RELATED_INVOICE_NUMBER,
      header: t("report.relatedInvoiceNumber"),
      width: 170,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.ADJUSTMENT_PERIOD_TYPE,
      header: t("report.adjustmentPeriodType"),
      width: 160,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.ADJUSTMENT_PERIOD_VALUE,
      header: t("report.adjustmentPeriod"),
      width: 150,
      sortable: false,
    },
    {
      field: OInvoiceLookUpField.NOTE,
      header: t("common.note"),
      width: 150,
      sortable: false,
    },
  ];

  const tableLastRow = (
    <ColumnGroup>
      <Row>
        <Column
          style={{
            justifyContent: "end",
            minWidth: 50,
            maxWidth: 50,
          }}
        />
        {summaryDetailColumns.map((column, index) => {
          return (
            // index columns
            <Column
              key={index}
              footer={
                <SumColumn
                  field={column.field}
                  value={{ totalAmount, totalAmountWithoutVAT, totalVatAmount }}
                />
              }
              style={{
                justifyContent: "end",
                minWidth: `${column.width}px`,
                maxWidth: `${column.width}px`,
              }}
            />
          );
        })}
      </Row>
    </ColumnGroup>
  );

  const {
    render: renderDataTable,
    reloadLazyData,
    selectedItems,
  } = useDataTable({
    indexColumnWidth: 50,
    heightCalc: 320,
    columns: summaryDetailColumns,
    footerColumnGroup: tableLastRow,
    getList: (params) => {
      const sums = {
        totalFee: 0,
        totalAmount: 0,
        totalAmountWithoutVAT: 0,
        totalVatAmount: 0,
      };
      summaryDetails.forEach((item) => {
        sums.totalFee += item.totalFee;
        sums.totalAmount += item.totalAmount;
        sums.totalAmountWithoutVAT += item.amountWithoutVAT;
        sums.totalVatAmount += item.vatAmount;
      });
      setTotalAmount(sums.totalAmount);
      setTotalAmountWithoutVAT(sums.totalAmountWithoutVAT);
      setTotalVatAmount(sums.totalVatAmount);
      const skipCount = params.skipCount;
      const maxResultCount = params.maxResultCount || 50; // Default to 50 items per page if not specified

      // First create the full indexingList with all items
      const fullIndexingList = summaryDetails.map((item, index) => {
        // set index for delete button
        return {
          ...item,
          index: index,
        };
      });

      // Then slice the array to get only the items for the current page
      const paginatedList = fullIndexingList.slice(
        skipCount,
        skipCount + maxResultCount
      );

      return Promise.resolve({
        items: paginatedList,
        totalCount: summaryDetails.length, // Keep the total count the same
      });
    },
  });

  const { renderImExExcel, showImportDialog } = useImExExcel({
    exportTemplate: () => {
      return InvoiceSummaryService.getExcelTemplate();
    },
    importExcel: (data) => {
      return InvoiceSummaryService.uploadExcel(data);
    },
    exportExcel() {
      return Promise.resolve();
    },
    reloadLazyData(result) {
      if (Array.isArray(result)) {
        setSummaryDetails(summaryDetails.concat(result));
        reloadLazyData();
      }
    },
    exportExcelFileName: "bang-tong-hop",
  });

  const { renderToolbar } = useToolbar({
    selectedItems,
    create() {
      setOpenLookup(true);
    },
    style: {
      borderWidth: 0,
    },
    otherActions: [
      {
        label: t("common.delete"),
        icon: "pi pi-trash",
        className: "iam-toolbar iam-toolbar-danger",
        disabled:
          (Array.isArray(selectedItems) && selectedItems.length === 0) ||
          !selectedItems,
        command: () => {
          // imported invoices from excel dont have invoice id
          // so we need to find the index of the item in the summaryDetails array
          if (Array.isArray(selectedItems)) {
            setSummaryDetails(
              summaryDetails.filter(
                (_item, index) =>
                  !(
                    selectedItems as (SummaryDataDetails & { index: number })[]
                  ).some((selected) => selected.index === index)
              )
            );
            setDeletedItems(selectedItems);
            reloadLazyData();
            return Promise.resolve();
          }

          setSummaryDetails(
            summaryDetails.filter((_item, index) => {
              return (
                index !==
                (selectedItems as SummaryDataDetails & { index: number }).index
              );
            })
          );
          setDeletedItems(selectedItems);
          reloadLazyData();
        },
      },
      {
        label: t("common.importExcel"),
        icon: "pi pi-file-excel",
        className: "iam-toolbar iam-toolbar-success",
        command: () => {
          showImportDialog();
        },
      },
    ],
  });

  useEffect(() => {
    if (lookupData.length > 0) {
      InvoiceSummaryService.getSummaryDetails({
        invoiceIdList: lookupData.map((item) => item.invoiceId),
        multipleTax: formState.multiTax,
        dataPeriod: formState.dataPeriod as DataPeriod,
        ...(formState.dataPeriod === ODataPeriod.DAY
          ? {
              dataPeriodDate: moment(formState.dataPeriodDate).format(
                "YYYY-MM-DD"
              ),
            }
          : {}),
        ...(formState.dataPeriod !== ODataPeriod.DAY
          ? { monthOrQuarter: parseInt(formState.monthOrQuarter) }
          : {}),
        ...(formState.dataPeriod !== ODataPeriod.DAY
          ? { year: formState.year }
          : {}),
        currency: formState.currencyCode,
        invoiceSerial: serial,
      }).then((data: SummaryDataDetails[]) => {
        if (id)
          setSummaryDetails(
            combineSummaryDetails(
              combineSummaryDetails(summaryDetails, currSummaryDetails),
              data
            )
          );
        else setSummaryDetails(combineSummaryDetails(summaryDetails, data));
        setLookupData([]);
        reloadLazyData();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupData, formState, id]);

  useEffect(() => {
    if (id && isEditMode) {
      InvoiceSummaryService.getInvoiceSummaryById(id).then((data) => {
        if (data.length > 0) {
          setCurrSummaryDetails(data);
          setSummaryDetails(data);
          if (setSelectedSerial) setSelectedSerial(data[0].invoiceSerial);
          reloadLazyData();
        }
      });
    } else {
      setSummaryDetails([]);
      reloadLazyData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode]);

  return (
    <>
      <div className='grid w-full'>
        <div className='col-12'>{renderToolbar()}</div>
        <div className='col-12 summary-data-detail-table'>
          {renderDataTable()}
        </div>
      </div>
      <InvoiceLookUpTableDialog
        open={openLookup}
        onHide={setOpenLookup}
        setLookupData={setLookupData}
        deletedItems={deletedItems}
        filterData={{
          year: formState.year,
          multiTax: formState.multiTax,
          dataPeriod: formState.dataPeriod,
          dataPeriodDate: formState.dataPeriodDate,
          monthOrQuarter: formState.monthOrQuarter,
          currencyCode: formState.currencyCode,
          serial: serial,
        }}
      />
      {renderImExExcel()}
    </>
  );
};
